<script setup lang="ts">
import { getLocalStorageItem } from '@sortera/ee-utils'
import { computed, ref } from 'vue'

import { UiAvatar } from '@/components/ui/UiAvatar/index.ts'
import { UiDialog } from '@/components/ui/UiDialog/index.ts'
import { UiIcon } from '@/components/ui/UiIcon/index.ts'
import { ROUTE_NAMES, UiRouterLink, useRoute, useRouter } from '@/services/routing/index.ts'
import { useAuth } from '@/services/use-auth.ts'
import { useUser } from '@/services/use-user.ts'

import { FeatHeadwayWidget } from '../FeatHeadwayWidget/index.ts'

import FeatNavbarFailureNotifications from './FeatNavbarFailureNotifications.vue'

interface Props {
  title: string
  showBackBtn: boolean
}

const { showBackBtn } = defineProps<Props>()

const route = useRoute()
const router = useRouter()
const { user } = useUser()

const isOpen = ref(false)

function iconClick(): void {
  if (showBackBtn) {
    if (route.name === ROUTE_NAMES.PARKED_LIST) {
      router.push({ name: ROUTE_NAMES.ORDER_LIST })
    } // TODO: this feels like a hack, fix it
    else router.back()
  } else router.push({ name: ROUTE_NAMES.HOME })
}

const currentVersion = computed(() => getLocalStorageItem('lastVersion'))

function dropdownItemClick(): void {
  const dropdownItem = document.activeElement
  if (!(dropdownItem instanceof HTMLElement)) return

  dropdownItem.blur()
}

function signOut(): Promise<void> {
  return useAuth().logout()
}
</script>

<template>
  <nav class="border-base-200 bg-base-100 sticky top-0 z-20 mx-auto w-full border-b shadow-sm">
    <UiDialog
      v-model:isOpen="isOpen"
      :title="$t('help.title')"
      @click-outside="isOpen = false"
    >
      <div class="collapse-plus bg-base-200 collapse">
        <input type="checkbox" />
        <div class="collapse-title font-medium">
          {{ $t('help.legends.title') }}
        </div>
        <div class="collapse-content">
          <table class="table-sm table">
            <thead>
              <tr>
                <th>{{ $t('help.legends.icon-column') }}</th>
                <th>{{ $t('help.legends.description-column') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-center">
                  <UiIcon name="mobile-alt" />
                </td>
                <td>{{ $t('help.legends.notified') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="truck" />
                </td>
                <td>{{ $t('help.legends.on-board') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="check" />
                </td>
                <td>{{ $t('help.legends.performed') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="exclamation-circle" />
                </td>
                <td>{{ $t('help.legends.littera-notes') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="exclamation-triangle" />
                </td>
                <td>{{ $t('help.legends.important-information') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="weight-hanging" />
                </td>
                <td>{{ $t('help.legends.heavy') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="industry" />
                </td>
                <td>{{ $t('help.legends.order-facility') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="building" />
                </td>
                <td>{{ $t('help.legends.client-name') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="id-badge" />
                </td>
                <td>{{ $t('help.legends.sales-person') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="user-circle" />
                </td>
                <td>{{ $t('help.legends.contact-on-site') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="user" />
                </td>
                <td>{{ $t('help.legends.client-contact') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="images" />
                </td>
                <td>{{ $t('help.legends.images') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="map-marked-alt" />
                </td>
                <td>{{ $t('help.legends.address') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="comment-alt" />
                </td>
                <td>{{ $t('help.legends.notes') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="directions" />
                </td>
                <td>{{ $t('help.legends.container-action') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="dumpster" />
                </td>
                <td>{{ $t('help.legends.container-type') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="eye" />
                </td>
                <td>{{ $t('help.legends.contents-material') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="bullseye" />
                </td>
                <td>{{ $t('help.legends.marking-to-look-for') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="rhombus" />
                </td>
                <td>{{ $t('help.legends.dangerous-material') }}</td>
              </tr>
              <tr>
                <td class="text-center">
                  <UiIcon name="cubes" />
                </td>
                <td>{{ $t('help.legends.cubic-meters') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <span>{{ $t('help.version-title') }}</span>
        <div class="flex font-mono text-sm text-gray-500">{{ currentVersion }}</div>
      </div>
      <div class="flex flex-col">
        {{ $t('help.contact-us') }}
        <a
          class="text-sortera-500 dark:text-sortera-600 underline hover:no-underline"
          href="mailto:dv@sortera.se"
        >
          dv@sortera.se
        </a>
      </div>
    </UiDialog>

    <div class="mx-auto flex flex-none items-center pl-2 sm:pl-4 lg:max-w-4xl xl:pl-6">
      <div
        class="logo-container group flex h-12 flex-none cursor-pointer items-center"
        @click="iconClick"
      >
        <a
          class="bg-sortera-500 flex h-full items-center justify-center px-3 text-gray-100 transition-all group-hover:px-2 group-hover:text-gray-200"
        >
          <UiIcon
            :class="{ 'text-xl': showBackBtn, 'text-lg': !showBackBtn }"
            :name="showBackBtn ? 'left' : 'truck'"
          />
        </a>
      </div>
      <div class="flex h-12 flex-auto items-center justify-between">
        <div class="mx-1 min-w-0 flex-1 text-2xl">{{ title.replace('#', '') }}</div>
        <div class="flex w-10 shrink-0 items-center justify-end gap-6 sm:w-20 lg:w-60 xl:w-72">
          <div class="flex items-stretch gap-1 sm:gap-3">
            <FeatNavbarFailureNotifications />
            <FeatHeadwayWidget
              id="changelog"
              account="7OdedJ"
            >
              <div class="flex items-center" />
            </FeatHeadwayWidget>
            <div class="dropdown dropdown-end">
              <label
                class="btn btn-square btn-ghost"
                tabindex="0"
              >
                <UiAvatar
                  class="h-8 w-8"
                  mask
                  :spacedName="
                    user?.name ??
                    // Needs some content so that the avatar is always visible.
                    // Important so that users can always find the menu options.
                    '?'
                  "
                />
              </label>
              <ul
                class="menu dropdown-content rounded-box bg-base-100 z-1 min-w-60 border border-gray-200 p-2 shadow-sm"
                tabindex="0"
              >
                <li>
                  <button @click="isOpen = true">{{ $t('help.title') }}</button>
                </li>
                <li>
                  <a
                    class="flex w-full justify-between gap-2"
                    href="https://www.sortera.se/sites/sortera/files/media/documents/recycling-tillstand-transport-avfall.pdf"
                    target="_blank"
                  >
                    {{ $t('nav.transport-permit') }}
                    <UiIcon
                      class="h-3"
                      name="external-link"
                    />
                  </a>
                </li>
                <li>
                  <a
                    class="flex w-full justify-between gap-2"
                    href="https://sorterase.sharepoint.com/sites/MGMT-Transport"
                    target="_blank"
                  >
                    {{ $t('nav.transport-security-instructions') }}
                    <UiIcon
                      class="h-3"
                      name="external-link"
                    />
                  </a>
                </li>
                <li v-if="user?.featureFlags?.developer">
                  <details>
                    <summary>{{ $t('nav.dev-section') }}</summary>
                    <ul>
                      <li @click="dropdownItemClick">
                        <UiRouterLink
                          class="whitespace-nowrap"
                          :to="{ name: ROUTE_NAMES.ROLES_AND_FLAGS }"
                        >
                          {{ $t('routes.roles-and-flags') }}
                        </UiRouterLink>
                      </li>
                      <li @click="dropdownItemClick">
                        <UiRouterLink
                          class="whitespace-nowrap"
                          :to="{ name: ROUTE_NAMES.TOOLS }"
                        >
                          {{ $t('routes.tools') }}
                        </UiRouterLink>
                      </li>
                    </ul>
                  </details>
                </li>
                <li
                  v-if="user?.roles?.admin"
                  @click="dropdownItemClick"
                >
                  <UiRouterLink
                    class="whitespace-nowrap"
                    :to="{ name: ROUTE_NAMES.ADMIN }"
                  >
                    {{ $t('routes.admin') }}
                  </UiRouterLink>
                </li>
                <li @click="dropdownItemClick">
                  <UiRouterLink
                    class="whitespace-nowrap"
                    :to="{ name: ROUTE_NAMES.SETTINGS }"
                  >
                    {{ $t('routes.settings') }}
                  </UiRouterLink>
                </li>
                <div class="divider my-0" />
                <li @click="dropdownItemClick">
                  <a
                    class="whitespace-nowrap"
                    @click="signOut"
                  >
                    {{ $t('auth.signout') }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<style>
@reference '@/assets/tailwind.css';

.logo-container {
  --logo-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M0,0h32L15.84,25.79c-2.39,3.81-6.56,6.14-11.06,6.18L0,32V0z'/%3E%3C/svg%3E");
  margin-left: -48px;

  &:before {
    @apply bg-sortera-500;
    content: '';
    display: inline-block;
    height: 100%;
    width: 48px;
    margin-left: -8px;
    mask-position: left;
    mask-repeat: no-repeat;
    mask-size: auto 100%;
    mask-image: var(--logo-mask);
    transform: rotateY(180deg);
  }

  &:after {
    @apply bg-sortera-500;
    content: '';
    display: inline-block;
    height: 100%;
    width: 48px;
    margin-left: -8px;
    mask-position: left;
    mask-repeat: no-repeat;
    mask-size: auto 100%;
    mask-image: var(--logo-mask);
  }
}
</style>
